import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Checkbox,
  Box,
  Flex,
  IconButton,
} from "@chakra-ui/react";
import { CustomText } from "../components";
import { useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
const getChildKey = (parentValue: string, childId: string) =>
  `${parentValue}:${childId}`;

export const StyledSelect = ({
  data = [],
  selectedValues = [],
  onChange = () => {},
  placeholder = "Select...",
  mt = 1,
}: any) => {
  const getAllValues = (items: any[]): string[] => {
    let values: string[] = [];
    items?.forEach((item) => {
      values?.push(item.value);
      if (item?.children && item?.children?.length > 0) {
        item?.children?.forEach((child: any) => {
          values?.push(getChildKey(item?.value, child?.value));
        });
        values = values?.concat(getAllValues(item?.children));
      }
    });
    return values;
  };

  const getItemAndChildrenValues = (item: any): string[] => {
    let values: string[] = [item?.value];
    if (item?.children && item?.children?.length > 0) {
      item?.children?.forEach((child: any) => {
        values?.push(getChildKey(item?.value, child?.value));
      });
    }
    return values;
  };

  const allValues = getAllValues(data);
  const [selected, setSelected] = useState<string[]>(selectedValues);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  const isAllSelected =
    allValues?.length > 0 && allValues?.every((v) => selected?.includes(v));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelected([]);
      onChange([]);
    } else {
      setSelected(allValues);
      onChange(allValues);
    }
  };

  const handleItemChange = (item: any) => {
    const valuesToToggle = getItemAndChildrenValues(item);
    const allSelected = valuesToToggle?.every((v) => selected?.includes(v));
    let newSelected;
    if (allSelected) {
      newSelected = selected?.filter((v) => !valuesToToggle?.includes(v));
    } else {
      newSelected = Array.from(new Set([...selected, ...valuesToToggle]));
    }
    setSelected(newSelected);
    onChange(newSelected);
  };

  const handleLeafChange = (parentValue: string, childId: string) => {
    const key = getChildKey(parentValue, childId);
    let newSelected;
    if (selected?.includes(key)) {
      newSelected = selected?.filter((v) => v !== key);
    } else {
      newSelected = [...selected, key];
    }
    setSelected(newSelected);
    onChange(newSelected);
  };

  const handleExpand = (value: string) => {
    setExpanded((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const renderChildItem = (parentValue: string, item: any) => {
    return (
      item?.children &&
      expanded[item?.value] && (
        <Box pl={4}>
          {item?.children?.map((child: any) => (
            <Flex
              align="center"
              py={1}
              key={getChildKey(item?.value, child?.value)}
            >
              <Checkbox
                isChecked={selected?.includes(
                  getChildKey(item?.value, child?.value)
                )}
                isIndeterminate={false}
                onChange={() => handleLeafChange(item?.value, child?.value)}
                colorScheme="purple"
                mr={2}
                sx={{
                  ".chakra-checkbox__control": {
                    borderColor: "#A7B2BA",
                    borderRadius: "sm",
                    bg: "transparent",
                  },
                  "&[data-checked] .chakra-checkbox__control": {
                    bg: "#1819B4",
                    borderColor: "#1819B4",
                    borderRadius: "sm",
                  },
                }}
                size="sm"
              />
              <CustomText
                text={child?.label}
                fontWeight={400}
                textColor={"#2F2F34"}
                fontSize="12px"
              />
            </Flex>
          ))}
        </Box>
      )
    );
  };

  const renderParentItems = (items: any[], level = 0, parentValue?: string) => (
    <Box pl={level * 4}>
      {items.map((item: any) => {
        const hasChildren = item.children && item.children.length > 0;
        const itemAndChildren = getItemAndChildrenValues(item);
        const isChecked = itemAndChildren.every((v) => selected.includes(v));

        return (
          <Box key={item.value}>
            <Flex align="center" py={1}>
              <Checkbox
                isChecked={isChecked}
                isIndeterminate={false}
                onChange={() => {
                  if (hasChildren) {
                    handleItemChange(item);
                  } else {
                    let newSelected;
                    if (selected.includes(item.value)) {
                      newSelected = selected.filter((v) => v !== item.value);
                    } else {
                      newSelected = [...selected, item.value];
                    }
                    setSelected(newSelected);
                    onChange(newSelected);
                  }
                }}
                colorScheme="purple"
                mr={2}
                sx={{
                  ".chakra-checkbox__control": {
                    borderColor: "#A7B2BA",
                    borderRadius: "sm",
                    bg: "transparent",
                  },
                  "&[data-checked] .chakra-checkbox__control": {
                    bg: "#1819B4",
                    borderColor: "#1819B4",
                    borderRadius: "sm",
                  },
                }}
                size="sm"
              />
              <Flex flex={1} align="center">
                <CustomText
                  text={item?.label}
                  fontWeight={400}
                  textColor={"#2F2F34"}
                  fontSize="12px"
                />
                {hasChildren ? (
                  <IconButton
                    aria-label={expanded[item?.value] ? "Collapse" : "Expand"}
                    icon={
                      expanded[item?.value] ? (
                        <ChevronDownIcon
                          ml={"-10px"}
                          boxSize={4}
                          color={"#676879"}
                        />
                      ) : (
                        <ChevronRightIcon
                          ml={"-10px"}
                          boxSize={4}
                          color={"#676879"}
                        />
                      )
                    }
                    size="xs"
                    variant="ghost"
                    onClick={() => handleExpand(item?.value)}
                    ml={2}
                  />
                ) : null}
              </Flex>
            </Flex>
            {renderChildItem(item?.value, item)}
            {item?.children?.some(
              (child: any) => child?.children && child?.children?.length > 0
            ) && renderParentItems(item?.children, level + 2, item?.value)}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Menu closeOnSelect={false} placement="bottom-end">
      {({ isOpen }) => (
        <>
          <MenuButton
            as="button"
            style={{
              padding: "8px 16px",
              marginLeft:"15px",
              borderRadius: "100px",
              border: `1px solid ${isOpen ? "#1819B4" : "#A7B2BA"}`,
              background: "#fff",
              height: "44px",
              fontWeight: 400,
              fontSize: "16px",
              color: "#5C6670",
              width: "150px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span>
              {placeholder}
              <ChevronDownIcon style={{ marginLeft: 8 }} />
            </span>
          </MenuButton>
          <MenuList
            minW="200px"
            border={"1px solid #CDD6DC"}
            borderRadius={"12px"}
            maxW="300px"
            mt={mt}
            boxShadow={"4px 4px 8px 0px #0000000F"}
          >
            <MenuItem _hover={{ bg: "transparent" }} closeOnSelect={false}>
              <Checkbox
                colorScheme="blue"
                isChecked={isAllSelected}
                isIndeterminate={false}
                onChange={handleSelectAll}
                mr={2}
                sx={{
                  ".chakra-checkbox__control": {
                    borderColor: "#A7B2BA",
                    borderRadius: "sm",
                    bg: "transparent",
                  },
                  "&[data-checked] .chakra-checkbox__control": {
                    bg: "#1819B4",
                    borderColor: "#1819B4",
                    borderRadius: "sm",
                  },
                }}
                size="sm"
              />
              <CustomText
                text="Select All"
                fontWeight={400}
                textColor={"#2F2F34"}
                fontSize="12px"
              />
            </MenuItem>
            <Box maxH="350px" pl={3} overflowY="auto" pr={2}>
              {renderParentItems(data)}
            </Box>
          </MenuList>
        </>
      )}
    </Menu>
  );
};
